/**
 * Live layout preview mocks — builder canvas inline preview.
 */

import { createScorerCoreMethods } from './scorer-core.js';
import { createScorerMatchScoreboardBindings } from './scorer-match-scoreboard.js';
import {
  SCORER_LAYOUT_FRAME_PRESETS,
  SCORER_LAYOUT_DESIGN_PROFILE,
  mergeLayoutProfileFrame,
  toLayoutThemeId,
  CUSTOM_LAYOUT_GRID_MARGIN_PX,
  CUSTOM_LAYOUT_GRID_RADIUS,
} from './scorer-layout-schema.js';
import { widgetShowLastScore } from './scorer-layout-widget-catalog.js';
import {
  computeBuilderGridMetrics,
  paintBuilderTrackOverlayCells,
} from './scorer-layout-gridstack-adapter.js';
import { scorerLayoutCatalogState } from './scorer-custom-layout.js';
import {
  PREVIEW_HOME_ID,
  PREVIEW_AWAY_ID,
  createPreviewMockMatch,
  createScorerLayoutPreviewSandboxMethods,
} from './scorer-layout-preview-sandbox.js';
import { playerAvatarUrl as resolvePlayerAvatarUrl } from '../admin/player-avatar-url.js';

export { scorerLayoutCatalogState, PREVIEW_HOME_ID, PREVIEW_AWAY_ID, createPreviewMockMatch };

/** Map builder profile key → kiosk tier attribute used by scorer CSS. */
export function resolveInlinePreviewKioskTier(profileKey) {
  return profileKey === 'tabletLandscape' ? 'tabletLandscape' : 'mobile';
}

/** Alpine methods for inline builder preview (matches standalone preview runtime). */
export function createScorerLayoutInlinePreviewMethods() {
  const core = createScorerCoreMethods({ scorerContext: 'match' });
  const sandbox = createScorerLayoutPreviewSandboxMethods();

  return {
    ...core,
    ...sandbox,
    ...createScorerMatchScoreboardBindings({
      useLayout: () => true,
      isLive: () => true,
      remainingForSide(side) {
        const id = side === 'home' ? PREVIEW_HOME_ID : PREVIEW_AWAY_ID;
        return this.match?.live?.currentRemaining?.[id] ?? 501;
      },
      sideName(side) {
        return side === 'home' ? 'Alex' : 'Jordan';
      },
      showVisitPills() {
        if (this._customLayoutScoreboardWidget) {
          return widgetShowLastScore(this._customLayoutScoreboardWidget);
        }
        return true;
      },
      visitPillsForSide(side) {
        const id = side === 'home' ? PREVIEW_HOME_ID : PREVIEW_AWAY_ID;
        return this.lastThreeVisitPillsForPlayer(id);
      },
    }),

    canvasPreviewMode: false,
    customLayoutPreviewSlug: '',
    scorerLayoutTheme: '',
    scoreInput: '',
    previewToastMessage: '',
    previewToastVisible: false,
    match: null,
    embedMode: true,
    fixture: null,
    editingHistoryIndex: null,

    structure() {
      return String(this.match?.format?.structure || 'legs');
    },

    bestOfLegsNum() {
      return Number(this.match?.format?.bestOfLegs) || 5;
    },

    matchAwaitingStart() {
      return false;
    },

    hasControl() {
      return true;
    },

    playerIsComputer() {
      return false;
    },

    legsWonForSide(side) {
      const homeId = PREVIEW_HOME_ID;
      const awayId = PREVIEW_AWAY_ID;
      if (side === 'away') return this.legsWonByPlayerId(awayId);
      return this.legsWonByPlayerId(homeId);
    },

    legsWonInCurrentSetForSide(side) {
      return this.legsWonForSide(side);
    },

    setWins(sideIndex) {
      void sideIndex;
      return 0;
    },

    headerRaceLeftText() {
      return `First to ${Math.ceil(this.bestOfLegsNum() / 2)} legs`;
    },

    showNextThrowerRow() {
      return true;
    },

    nextPlayerName() {
      const turnId = this.currentTurnId();
      const nextId = turnId === PREVIEW_AWAY_ID ? PREVIEW_HOME_ID : PREVIEW_AWAY_ID;
      return this.playerName(nextId);
    },

    nextPlayerAvatarUrl() {
      const turnId = this.currentTurnId();
      const nextId = turnId === PREVIEW_AWAY_ID ? PREVIEW_HOME_ID : PREVIEW_AWAY_ID;
      return this.playerAvatarUrl(this.playerForId(nextId));
    },

    scorerTabletKioskRaceBarVisible() {
      if (this.canvasPreviewMode) return true;
      if (typeof this.scorerLayoutUsesDefaultTabletHeader === 'function' && this.scorerLayoutUsesDefaultTabletHeader()) {
        return true;
      }
      return false;
    },

    scorerTabletKioskRaceBarShowNextThrower() {
      return this.scorerTabletKioskRaceBarVisible() && this.showNextThrowerRow();
    },

    tabletKioskRaceSideSetsValue(side) {
      return this.setWins(side === 'away' ? 1 : 0);
    },

    tabletKioskRaceSideLegsValue(side) {
      return this.legsWonForSide(side);
    },

    tabletKioskRaceLegsBestOfLabel() {
      return `Bo ${this.bestOfLegsNum()}`;
    },

    tabletKioskRaceMatchHint() {
      const text = String(this.headerRaceLeftText?.() || '').trim();
      return text || 'In progress';
    },

    startHistoryEdit() {},

    resolveCustomLayoutProfileKey() {
      if (typeof this.previewFrameKey === 'function') {
        return this.previewFrameKey();
      }
      if (typeof this.activeProfile === 'string' && this.activeProfile) {
        return this.activeProfile === 'mobilePortrait' ? 'mobilePortrait' : 'tabletLandscape';
      }
      return core.resolveCustomLayoutProfileKey.call(this);
    },

    scorerLayoutThemeId() {
      if (this.customLayoutPreviewSlug) return toLayoutThemeId(this.customLayoutPreviewSlug);
      return core.scorerLayoutThemeId.call(this);
    },

    isCustomScorerLayoutActive() {
      if (this.canvasPreviewMode && this.customLayoutPreviewSlug) return true;
      return core.isCustomScorerLayoutActive.call(this);
    },

    scorerUsesCustomLayoutShell() {
      if (this.canvasPreviewMode) return Boolean(this.customLayoutPreviewSlug);
      return core.scorerUsesCustomLayoutShell.call(this);
    },

    inlinePreviewProfileData() {
      const design = typeof this.designProfileData === 'function'
        ? this.designProfileData()
        : (typeof this.activeProfileData === 'function' ? this.activeProfileData() : null);
      if (!design) return this.activeCustomLayoutProfile();
      const frameKey = typeof this.previewFrameKey === 'function'
        ? this.previewFrameKey()
        : (this.activeProfile || SCORER_LAYOUT_DESIGN_PROFILE);
      return mergeLayoutProfileFrame(design, frameKey);
    },

    activeCustomLayoutProfile() {
      if (this.canvasPreviewMode) {
        return this.inlinePreviewProfileData();
      }
      return core.activeCustomLayoutProfile.call(this);
    },

    inlinePreviewKioskTier() {
      return resolveInlinePreviewKioskTier(this.resolveCustomLayoutProfileKey());
    },

    syncInlinePreviewDeviceContext(root = this.$refs?.inlinePreviewHost) {
      if (!this.canvasPreviewMode || !root) return;
      const tier = this.inlinePreviewKioskTier();
      root.dataset.kioskTier = tier;
      root.dataset.kioskLayout = 'v2';
      const profileKey = this.resolveCustomLayoutProfileKey();
      root.dataset.profile = profileKey;
      root.dataset.customLayoutProfile = profileKey;
      root.classList.toggle('scorer-tablet-wide', this.scorerUseTabletWideLayout());
    },

    _scorerViewportContext() {
      if (this.canvasPreviewMode) {
        const profileKey = this.resolveCustomLayoutProfileKey();
        const frame = this.inlinePreviewProfileData()?.frame || SCORER_LAYOUT_FRAME_PRESETS[profileKey];
        return {
          width: Number(frame?.safeWidth) || 366,
          height: Number(frame?.safeHeight) || 760,
          embedMode: false,
          fromVenueKiosk: false,
          portraitMedia: profileKey === 'mobilePortrait',
        };
      }
      return core._scorerViewportContext.call(this);
    },

    inlinePreviewGridMetrics() {
      const profileKey = this.resolveCustomLayoutProfileKey();
      const profile = this.inlinePreviewProfileData();
      const scale = Number(this.canvasScale) || 1;
      return computeBuilderGridMetrics(profile, profileKey, scale);
    },

    inlinePreviewDeviceFrameStyle() {
      const metrics = this.inlinePreviewGridMetrics();
      return {
        width: `${metrics.width}px`,
        height: `${metrics.height}px`,
        maxWidth: '100%',
      };
    },

    inlinePreviewDeviceShellStyle() {
      return this.inlinePreviewDeviceFrameStyle();
    },

    inlinePreviewTrackOverlayStyle() {
      const metrics = this.inlinePreviewGridMetrics();
      return {
        width: `${metrics.width}px`,
        height: `${metrics.height}px`,
        maxWidth: '100%',
      };
    },

    customLayoutFrameVars() {
      if (!this.canvasPreviewMode) return core.customLayoutFrameVars.call(this);
      return {
        width: '100%',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        '--custom-layout-grid-margin': `${CUSTOM_LAYOUT_GRID_MARGIN_PX}px`,
        '--custom-layout-grid-radius': CUSTOM_LAYOUT_GRID_RADIUS,
      };
    },

    customLayoutGridStyle() {
      if (!this.canvasPreviewMode) return core.customLayoutGridStyle.call(this);
      const prof = this.inlinePreviewProfileData();
      const grid = prof?.grid || {};
      const cols = Math.max(1, Number(grid.columns) || 12);
      const rows = Math.max(1, Number(grid.rows) || 8);
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        width: '100%',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
        gap: 0,
        boxSizing: 'border-box',
      };
    },

    paintInlinePreviewTrackOverlay(root = this.$refs?.inlinePreviewHost) {
      const overlay = root?.querySelector?.('.scorer-layout-builder-track-overlay');
      if (!overlay) return;
      paintBuilderTrackOverlayCells(overlay, this.inlinePreviewGridMetrics());
    },

    kioskTier() {
      return this.inlinePreviewKioskTier();
    },

    rosterDensity() {
      return 'singles';
    },

    isVenueKioskEmbed() {
      if (this.canvasPreviewMode) return false;
      return true;
    },

    scorerHeaderShowVenueEmbedBar() {
      return false;
    },

    scorerHeaderUseFixtureEmbedLayout() {
      return false;
    },

    scorerHeaderShowClose() {
      return false;
    },

    liveKeypadEnabled() {
      return true;
    },

    liveScorerKeypadEnabled() {
      return true;
    },

    liveKeypadEnabledNative() {
      return true;
    },

    liveScoringKeypadEnabled() {
      return true;
    },

    pid(id) {
      return String(id || '');
    },

    playerName(id) {
      const pid = this.pid(id);
      const row = (this.match?.players || []).find((p) => this.pid(p.id) === pid);
      return row?.name || 'Player';
    },

    currentTurnId() {
      return String(this.match?.live?.currentTurnPlayerId || PREVIEW_HOME_ID);
    },

    playerForId(id) {
      const pid = this.pid(id);
      return (this.match?.players || []).find((p) => this.pid(p.id) === pid) || null;
    },

    playerSideForId(playerId) {
      const row = this.playerForId(playerId);
      if (row?.side === 'away') return 'away';
      if (row?.side === 'home') return 'home';
      return null;
    },

    remainingForPlayerId(id) {
      const pid = this.pid(id);
      return this.match?.live?.currentRemaining?.[pid] ?? this.startingScore();
    },

    matchFinished() {
      return false;
    },

    openPlayerStatsForPlayer() {},

    playerAvatarUrl(p) {
      if (!p) return resolvePlayerAvatarUrl(null);
      return resolvePlayerAvatarUrl({
        playerId: this.pid(p.id),
        avatarSeed: p.avatarSeed,
        imageUrl: p.avatarUrl || p.avatarImageUrl,
      });
    },

    formatStatAvg(n) {
      if (n == null || Number.isNaN(Number(n))) return '—';
      return Number(n).toFixed(1);
    },

    panelCheckoutSuggestionForPlayer() {
      return 'T20 · T19 · D12';
    },

    sidePlayers(side) {
      return (this.match?.players || []).filter((p) => String(p.side) === side);
    },

    currentTurnSide() {
      const turnId = String(this.match?.live?.currentTurnPlayerId || PREVIEW_HOME_ID);
      const row = (this.match?.players || []).find((p) => this.pid(p.id) === turnId);
      return row?.side === 'away' ? 'away' : 'home';
    },

    myRemaining() {
      return this.remainingForSide(this.currentTurnSide());
    },

    startingScore() {
      return Number(this.match?.format?.startingScore) || 501;
    },

    scorerUseTabletWideLayout() {
      return this.resolveCustomLayoutProfileKey() === 'tabletLandscape';
    },

    isMobilePortraitLayout() {
      return this.resolveCustomLayoutProfileKey() === 'mobilePortrait';
    },

    scorerUseKioskScoringGrid() {
      return true;
    },

    showInlinePreviewToast(message) {
      const text = String(message || '').trim();
      if (!text) return;
      this.previewToastMessage = text;
      this.previewToastVisible = true;
      if (this._inlinePreviewToastTimer) clearTimeout(this._inlinePreviewToastTimer);
      this._inlinePreviewToastTimer = setTimeout(() => {
        this.previewToastVisible = false;
      }, 2400);
    },
  };
}
